/**
 * SOLID Review
 * S — Strong. One concern: the client's TABLE as data — what a table decision reads (the snapshot),
 *     what it writes (the changes) and what it derives for the screen (the derived values).
 * O — Good. A new table field is one key in `ClientTable`; the changes schema follows from it.
 * L — N/A — no inheritance hierarchy.
 * I — Good. Three schemas a caller needs together, and the small ones they are built from.
 * D — Good. zod, the shared primitives and the Domain's own enums.
 */
import { z } from 'zod';
import { GamePhase, PlayerStatus, MonetizationTrack } from '../../uml-components/primitives';
import { Identifier, MeyerRank, DieFace } from './primitives';
import { ClientDiceRanking } from './DiceRankingMoves';

/**
 * THE TABLE AS THE CLIENT HOLDS IT (frontend strict-ports plan, FE1, 2026-10-03).
 *
 * One snapshot for every table decision, the client form of the backend's `RoundGameInput`: the
 * Application loads it through `ClientState.snapshot`, a decision reads it, and the changes it returns
 * are saved through `ClientState.apply`. Its fields are today's game-store fields that the table loop
 * reads or writes, under their store names, plus four that left React state or a hook's ref:
 *
 *   - `opponentClaim` and `revealResult` were ControlPanel's `useState`, seeded from the store's
 *     `pendingOpponentClaim`/`pendingRevealResult` by two effects. They are written once now, by the
 *     decisions, and the two pending fields and both seeding effects go.
 *   - `roomEnded` was AppRoot's `showRoomEnded`.
 *   - `tableVersion` was `useTableView`'s `lastVersion` ref. It carries its room, so a view for another
 *     room starts again from 0, which is what the ref did by re-subscribing.
 *   - `connectionWasDown` was `useWsConnectionSync`'s `wasDown` ref: the latch that turns a RE-connect
 *     into a resync. In the store it survives a re-subscription without needing a ref.
 *
 * `room` has no `createdAt`: a `Date` is not JSON, and only tests ever read it.
 */

const ClientPhase = z.enum(GamePhase).meta({ id: 'ClientPhase' });

/** A seat at the table — `IEntityPlayer` as JSON. */
const ClientSeat = z.object({
  id: Identifier,
  displayName: z.string(),
  avatarUrl: z.string().nullable(),
  status: z.enum(PlayerStatus),
  lives: z.object({ current: z.number().int(), max: z.number().int(), isEliminated: z.boolean() }),
  monetization: z.object({ track: z.enum(MonetizationTrack), matchCredits: z.number(), hasPremiumAccess: z.boolean() }),
  isLocalPlayer: z.boolean(),
  isCurrentTurn: z.boolean(),
  seatIndex: z.number().int(),
}).meta({ id: 'ClientSeat' });

const ClientRoom = z.object({
  roomId: Identifier,
  roomCode: z.string(),
  hostPlayerId: z.string(),
  players: z.array(ClientSeat),
  maxPlayers: z.number().int(),
  minPlayers: z.number().int(),
  isGameActive: z.boolean(),
}).meta({ id: 'ClientRoom' });

/** The dice the local roller has seen, or the reveal's dice once the cup is lifted. */
export const ClientDiceRoll = z.object({
  die1: DieFace,
  die2: DieFace,
  score: MeyerRank,
  isMeyer: z.boolean(),
  isDouble: z.boolean(),
}).meta({ id: 'ClientDiceRoll' });

/** The claim this client is asked to believe or lift. */
const ClientOpponentClaim = z.object({ score: MeyerRank, label: z.string() }).meta({ id: 'ClientOpponentClaim' });

/** What the last lift showed — on screen through REVEALING and GAME_OVER, and Play Again's next roller. */
const ClientReveal = z.object({
  losingPlayerId: z.string(),
  wasBluff: z.boolean(),
  claimedScore: MeyerRank,
  trueScore: MeyerRank,
  die1: DieFace,
  die2: DieFace,
  newRollerId: z.string(),
}).meta({ id: 'ClientReveal' });

const ClientDecisionCountdown = z.object({
  roundId: z.string(),
  challengerId: z.string(),
  secondsRemaining: z.number().int(),
}).meta({ id: 'ClientDecisionCountdown' });

const ClientConnectionState = z.enum(['connecting', 'connected', 'disconnected', 'reconnecting', 'failed']).meta({ id: 'ClientConnectionState' });

/** The newest pushed table view applied, and the room it belongs to (backend §28.192). */
const ClientTableVersion = z.object({ roomId: Identifier, version: z.number().int() }).meta({ id: 'ClientTableVersion' });

export const ClientTable = z.object({
  /** Who this client is; null before identity. Read by the decisions, written only by the identity flows (FE2). */
  localPlayerId: Identifier.nullable(),
  room: ClientRoom.nullable(),
  phase: ClientPhase,
  activeRoundId: z.string().nullable(),
  activeRollerId: z.string().nullable(),
  /** The server's decider for the pending claim; null falls back to the local rotation. Never ''. */
  activeChallengerId: z.string().nullable(),
  lastDiceRoll: ClientDiceRoll.nullable(),
  minimumClaimScore: MeyerRank,
  lastClaimedScore: MeyerRank.nullable(),
  claimWasBlind: z.boolean(),
  diceRevealed: z.boolean(),
  opponentClaim: ClientOpponentClaim.nullable(),
  revealResult: ClientReveal.nullable(),
  decisionCountdown: ClientDecisionCountdown.nullable(),
  playerLives: z.record(z.string(), z.number().int()),
  suspendedPlayerIds: z.array(z.string()),
  roomEnded: z.boolean(),
  tableVersion: ClientTableVersion.nullable(),
  connectionState: ClientConnectionState,
  connectionWasDown: z.boolean(),
  /**
   * When the table last moved (epoch ms) — stamped by the store on a decision's `markActivity`, READ by the
   * media's idle rule (FE5c), written by no decision: like `localPlayerId`, it is not in the changes.
   */
  lastGameActivityAt: z.number(),
}).meta({ id: 'ClientTable' });

/** A legal claim, as the claim picker lists it. */
const ClientClaimOption = z.object({
  score: MeyerRank,
  label: z.string(),
  /** The combination the roller actually rolled — the picker marks it. */
  isRolled: z.boolean(),
}).meta({ id: 'ClientClaimOption' });

/**
 * WHAT THE SCREEN NEEDS THAT ONLY THE RULES CAN SAY. Computed by the Domain from the table after every
 * decision and saved with it, so the Framework reads answers rather than importing the rules that give
 * them (`framework-no-domain-deps`, FE7). Every value here was computed inline in a component before:
 * the challenger in ControlPanel and AppRoot (twice, the same way), the blocking opponent in AppRoot,
 * the claim lists, labels and dice in ControlPanel, the cup's action in GameScene and its fallback.
 */
const ClientTableDerived = z.object({
  /** The server's decider when it has named one, otherwise the next live, non-suspended seat after the roller. */
  challengerId: z.string().nullable(),
  isChallenger: z.boolean(),
  isActiveRoller: z.boolean(),
  /** The suspended opponent the current phase is waiting on, if any — the "waiting for…" overlay. */
  blockingOpponentId: z.string().nullable(),
  /** What a tap on the cup does now: roll, peek, or nothing. */
  cupAction: z.enum(['roll', 'peek']).nullable(),
  /** Every claim that beats the floor, in rank order — the blind picker's list. */
  legalClaims: z.array(ClientClaimOption),
  /** With the dice seen and a floor to beat: the legal claims BELOW the rolled score (a bluff)… */
  tacticalClaims: z.array(ClientClaimOption),
  /** …and the rest. Without a floor, every legal claim is here. */
  honestClaims: z.array(ClientClaimOption),
  rolledLabel: z.string().nullable(),
  /** The claimed combination as two dice, high first — the decider's "claimed" display. */
  opponentClaimDice: z.tuple([DieFace, DieFace]).nullable(),
  /** Meyer cannot be believed, only lifted. */
  canBelieve: z.boolean(),
  revealLabels: z.object({ claimed: z.string(), actual: z.string() }).nullable(),
}).meta({ id: 'ClientTableDerived' });

/**
 * What a table decision changes. Every table field may change except `localPlayerId`, which is
 * identity. Three instructions ride along:
 *
 *   - `reset`: back to the store's initial state first (the store's `resetAll`), then the rest.
 *   - `markActivity`: the table moved. The store stamps `lastGameActivityAt`, which LiveKit's idle
 *     disconnect reads. A decision does not read clocks.
 *   - `derived`: the derived values for the table as it stands AFTER these changes.
 */
export const ClientTableChanges = ClientTable.omit({ localPlayerId: true, lastGameActivityAt: true }).partial().extend({
  reset: z.literal(true).optional(),
  markActivity: z.literal(true).optional(),
  derived: ClientTableDerived.optional(),
}).meta({ id: 'ClientTableChanges' });

/**
 * Every table decision's input: the table, as `ClientState.snapshot` returns it, and the rules it is played by — the
 * session's dice ranking (frontend-domain-oo P2). The claim lists, the labels, the claimed dice and the Meyer
 * pre-check read the ranking, and a table decision is not handed the session.
 */
export const ClientTableInput = z.object({ table: ClientTable, diceRanking: ClientDiceRanking }).meta({ id: 'ClientTableInput' });

export type IStateClientTable = z.infer<typeof ClientTable>;
export type IPropsClientTableInput = z.infer<typeof ClientTableInput>;
export type IEntityClientTableDerived = z.infer<typeof ClientTableDerived>;
export type IEntityClientClaimOption = z.infer<typeof ClientClaimOption>;
export type IEntityClientDiceRoll = z.infer<typeof ClientDiceRoll>;
export type IEntityClientReveal = z.infer<typeof ClientReveal>;
export type IEntityClientRoom = z.infer<typeof ClientRoom>;
export type IEntityClientSeat = z.infer<typeof ClientSeat>;
